import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Tab,
  Tabs,
  Theme,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState, useCallback } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateRole from "./CreateRole";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  adminCreateRoles,
  adminGetRoles,
  adminUpdateRoles,
  deleteAdminRole,
} from "./roleAndPermissionActions";
import { RootState } from "../../store/store";
import Permissions from "./Permissions";
import AuditLog from "./AuditLog";
import AssignedUsers from "./AssignedUsers";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import RoleAndPermissionStyles from "./RoleAndPermissionStyles";

const RoleAndPermission = () => {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { adminRoleData } = useAppSelector(
    (state: RootState) => state.adminRoles
  );
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (state) => state.common
  );

  const summaryData = [
    {
      label: "Total Roles",
      value: adminRoleData.totalRoles,
      sub: `${
        adminRoleData?.rolePermissionsResponses?.filter(
          (r) => r.type === "System"
        ).length
      } system and ${
        adminRoleData?.rolePermissionsResponses?.filter(
          (r) => r.type === "Custom"
        ).length
      } custom roles`,
      icon: <ShieldOutlinedIcon sx={{ fontSize: 32, color: "#23272E" }} />,
    },
    {
      label: "Assigned Users",
      value: adminRoleData.totalUsers,
      sub: "Across all roles",
      icon: <GroupOutlinedIcon sx={{ fontSize: 32, color: "#23272E" }} />,
    },
    {
      label: "Total Permissions",
      value: adminRoleData.totalPermissions,
      sub: "Distributed across roles",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 32, color: "#23272E" }} />,
    },
    {
      label: "Audit Logs",
      value: adminRoleData.auditLogsCount,
      sub: `Last activity: ${new Date(
        adminRoleData.lastModifiedAuditLogDate
      ).toLocaleString()}`,
      icon: <HistoryOutlinedIcon sx={{ fontSize: 32, color: "#23272E" }} />,
    },
  ];
  // State for Create Role dialog
  const [openCreateRole, setOpenCreateRole] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRoleData, setEditRoleData] = useState<any>(null);
  const dispatch = useAppDispatch();

  // Handler for opening the create role dialog
  const handleOpenCreateRole = () => {
    setEditMode(false);
    setEditRoleData(null);
    setOpenCreateRole(true);
  };

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    dispatch(adminGetRoles());
  };
  // Handler for opening the edit role dialog
  const handleEditClick = useCallback((role: any) => {
    setEditMode(true);
    setEditRoleData({
      name: role.name,
      description: role.description,
      permissions: Array.isArray(role.permissions) ? role.permissions : [],
    });
    setOpenCreateRole(true);
  }, []);

  // Handler for creating or updating a role
  const handleCreateOrUpdateRole = (roleData: any) => {
    if (editMode) {
      dispatch(adminUpdateRoles(roleData));
    } else {
      dispatch(adminCreateRoles(roleData));
    }
    setOpenCreateRole(false);
    setEditRoleData(null);
    setEditMode(false);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string>("");

  // Handler for delete icon click
  const handleDeleteClick = (roleName: string) => {
    setRoleToDelete(roleName);
    setDeleteDialogOpen(true);
  };

  // Handler for confirming delete
  const handleConfirmDelete = () => {
    dispatch(deleteAdminRole(roleToDelete));
    setDeleteDialogOpen(false);
    setRoleToDelete("");
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete("");
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      (calledReducerType === "roles/adminCreateRoles" ||
        calledReducerType === "roles/deleteAdminRole" ||
        calledReducerType === "roles/adminUpdateRoles")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);
  useEffect(() => {
    dispatch(adminGetRoles());
  }, []);

  return (
    <>
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Box
          sx={RoleAndPermissionStyles.pageHeader}
          id="roleandpermissions_header"
          data-test-id="test-roleandpermissions_header"
          aria-label="Roles and Permissions Header"
        >
          <Typography
            variant="h6"
            sx={RoleAndPermissionStyles.pageTitle(theme)}
            id="roleandpermissions_title"
            data-test-id="test-roleandpermissions_title"
            aria-label="Roles and Permissions Title"
          >
            Roles & Permissions
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={RoleAndPermissionStyles.createRoleButton}
            onClick={handleOpenCreateRole}
            id="roleandpermissions_button-create-role"
            data-test-id="test-roleandpermissions_button-create-role"
            aria-label="Click to create new role"
          >
            + Create Role
          </Button>
        </Box>

        <Grid
          container
          spacing={2}
          sx={RoleAndPermissionStyles.summaryGrid}
          id="roleandpermissions_summary-grid"
          data-test-id="test-roleandpermissions_summary-grid"
          aria-label="Summary Grid for Roles and Permissions"
        >
          {summaryData.map((item) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={item.label}
              id={`roleandpermissions_summary-item-${item.label}`}
              data-test-id={`test-roleandpermissions_summary-item-${item.label}`}
              aria-label={`Summary card for ${item.label}`}
            >
              <Paper sx={RoleAndPermissionStyles.summaryCard}>
                <Box sx={RoleAndPermissionStyles.summaryCardHeader}>
                  <Typography
                    variant="subtitle1"
                    sx={RoleAndPermissionStyles.summaryCardLabel}
                  >
                    {item.label}
                  </Typography>
                  {item.icon}
                </Box>
                <Typography
                  variant="h4"
                  sx={RoleAndPermissionStyles.summaryCardValue}
                >
                  {item.value}
                </Typography>
                <Typography sx={RoleAndPermissionStyles.summaryCardSub}>
                  {item.sub}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper sx={RoleAndPermissionStyles.tabsPaper}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            id="roleandpermissions_tabs"
            data-test-id="test-roleandpermissions_tabs"
            aria-label="Roles and Permissions Tab Navigation"
          >
            <Tab
              label="Roles"
              id="roleandpermissions_tab-roles"
              data-test-id="test-roleandpermissions_tab-roles"
              aria-label="Switch to Roles tab"
            />
            <Tab
              label="Permissions"
              id="roleandpermissions_tab-permissions"
              data-test-id="test-roleandpermissions_tab-permissions"
              aria-label="Switch to Permissions tab"
            />
            <Tab
              label="Assigned Users"
              id="roleandpermissions_tab-assigned-users"
              data-test-id="test-roleandpermissions_tab-assigned-users"
              aria-label="Switch to Assigned Users tab"
            />
            <Tab
              label="Audit Log"
              id="roleandpermissions_tab-audit-log"
              data-test-id="test-roleandpermissions_tab-audit-log"
              aria-label="Switch to Audit Log tab"
            />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <>
            <TextField
              placeholder="Search roles..."
              size="small"
              sx={RoleAndPermissionStyles.tableSearch}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              id="roleandpermissions_textfield-search-roles"
              data-test-id="test-roleandpermissions_textfield-search-roles"
              aria-label="Enter text to search roles"
            />
            {isMobile ? (
              <Box>
                {adminRoleData.rolePermissionsResponses.filter((role) =>
                  role.name.toLowerCase().includes(search.toLowerCase())
                ).length === 0 ? (
                  <Paper
                    sx={RoleAndPermissionStyles.noRoleFoundPaper}
                    id="roleandpermissions_paper-no-roles"
                    data-test-id="test-roleandpermissions_paper-no-roles"
                    aria-label="No roles found"
                  >
                    <Typography>No Role found.</Typography>
                  </Paper>
                ) : (
                  adminRoleData.rolePermissionsResponses
                    .filter((role) =>
                      role.name.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((role) => (
                      <Paper
                        key={role.name}
                        sx={RoleAndPermissionStyles.mobileRoleCard}
                        id={`roleandpermissions_mobile-rolecard-${role.name}`}
                        data-test-id={`test-roleandpermissions_mobile-rolecard-${role.name}`}
                        aria-label={`Mobile card for role ${role.name}`}
                      >
                        <Box sx={RoleAndPermissionStyles.mobileRoleCardHeader}>
                          <Typography fontWeight="bold">{role.name}</Typography>
                          <Chip
                            label={role.type}
                            color={
                              role.type === "System" ? "default" : "primary"
                            }
                            size="small"
                            id={`roleandpermissions_chip-role-type-${role.name}`}
                            data-test-id={`test-roleandpermissions_chip-role-type-${role.name}`}
                            aria-label={`Role type: ${role.type}`}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {role.description}
                        </Typography>
                        <Box sx={RoleAndPermissionStyles.mobileRoleCardInfo}>
                          <Typography variant="caption">
                            Users: {role.totalUsersPerRole}
                          </Typography>
                          <Typography variant="caption">
                            Permissions: {role.permissionsCount}
                          </Typography>
                          <Typography variant="caption">
                            Created:{" "}
                            {new Date(role.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(role)}
                            id={`roleandpermissions_iconbutton-edit-${role.name}`}
                            data-test-id={`test-roleandpermissions_iconbutton-edit-${role.name}`}
                            aria-label={`Edit role ${role.name}`}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(role.name)}
                            id={`roleandpermissions_iconbutton-delete-${role.name}`}
                            data-test-id={`test-roleandpermissions_iconbutton-delete-${role.name}`}
                            aria-label={`Delete role ${role.name}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))
                )}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table
                  id="roleandpermissions_table-roles"
                  data-test-id="test-roleandpermissions_table-roles"
                  aria-label="Roles table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Role Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Assigned Users</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminRoleData.rolePermissionsResponses.filter((role) =>
                      role.name.toLowerCase().includes(search.toLowerCase())
                    ).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No Role found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      adminRoleData.rolePermissionsResponses
                        .filter((role) =>
                          role.name.toLowerCase().includes(search.toLowerCase())
                        )
                        .map((role) => (
                          <TableRow
                            key={role.name}
                            id={`roleandpermissions_tablerow-role-${role.name}`}
                            data-test-id={`test-roleandpermissions_tablerow-role-${role.name}`}
                            aria-label={`Row for role ${role.name}`}
                          >
                            <TableCell>
                              <Typography fontWeight="bold">
                                {role.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{role.description}</TableCell>
                            <TableCell>{role.totalUsersPerRole}</TableCell>
                            <TableCell>{role.permissionsCount}</TableCell>
                            <TableCell>
                              <Chip
                                label={role.type}
                                color={
                                  role.type === "System" ? "default" : "primary"
                                }
                                size="small"
                                id={`roleandpermissions_chip-type-${role.name}`}
                                data-test-id={`test-roleandpermissions_chip-type-${role.name}`}
                                aria-label={`Role type: ${role.type}`}
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(role.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(role)}
                                disabled={role.name === "Super Admin"}
                                id={`roleandpermissions_iconbutton-edit-table-${role.name}`}
                                data-test-id={`test-roleandpermissions_iconbutton-edit-table-${role.name}`}
                                aria-label={`Edit role ${role.name}`}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(role.name)}
                                disabled={role.name === "Super Admin"}
                                id={`roleandpermissions_iconbutton-delete-table-${role.name}`}
                                data-test-id={`test-roleandpermissions_iconbutton-delete-table-${role.name}`}
                                aria-label={`Delete role ${role.name}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleCancelDelete}
              aria-labelledby="delete-role-dialog-title"
              id="roleandpermissions_dialog-delete-role"
              data-test-id="test-roleandpermissions_dialog-delete-role"
              aria-label="Delete role confirmation dialog"
            >
              <DialogTitle id="delete-role-dialog-title">
                Confirm Delete
              </DialogTitle>
              <DialogContent>
                Are you sure you want to delete the role <b>{roleToDelete}</b>?
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCancelDelete}
                  variant="outlined"
                  sx={{ borderRadius: "4px" }}
                  id="roleandpermissions_button-cancel-delete"
                  data-test-id="test-roleandpermissions_button-cancel-delete"
                  aria-label="Cancel delete role"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  sx={{ borderRadius: "4px" }}
                  variant="contained"
                  color="error"
                  id="roleandpermissions_button-confirm-delete"
                  data-test-id="test-roleandpermissions_button-confirm-delete"
                  aria-label="Confirm delete role"
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {tab === 1 && <Permissions />}
        {tab === 2 && <AssignedUsers />}
        {tab === 3 && <AuditLog />}

        <CreateRole
          open={openCreateRole}
          onClose={() => {
            setOpenCreateRole(false);
            setEditRoleData(null);
            setEditMode(false);
          }}
          onCreate={handleCreateOrUpdateRole}
          editMode={editMode}
          initialData={editRoleData}
        />
      </Box>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </>
  );
};

export default RoleAndPermission;
